import { MEDIA_VIEWING_CONFIGURED, mediaSrc, useMediaTicket } from "../lib/media.js";
import { MEDIA_KIND_AR, UPLOAD_STATE_AR, fmtDateTime } from "../lib/format.js";
import type { MediaRow, UploadState } from "../lib/types.js";
import { Badge, Note, cx } from "./kit.js";
import type { Tone } from "./kit.js";

/**
 * SYNC.md Rule 3 in one component.
 *
 * "The server accepts a job completion whose photos have not arrived yet. The job row is
 * `synced`; the media is `pending`. These are different fields and the office UI shows both."
 * So every tile states its upload state out loud, and a pending tile never renders a frame
 * that could be mistaken for a photograph that exists. The office must never read "pending"
 * as "not done" — nor "pending" as "here it is".
 */

const TONE: Record<UploadState, Tone> = {
  pending: "warn",
  uploading: "info",
  stored: "good",
  failed: "bad",
};

export function Photo({ media, className }: { media: MediaRow; className?: string }) {
  const ticket = useMediaTicket();
  const src = mediaSrc(media, ticket);
  const label = MEDIA_KIND_AR[media.kind] ?? media.kind;

  return (
    <figure className={cx("overflow-hidden rounded-md border border-line bg-surface", className)}>
      <div className="relative flex aspect-4/3 items-center justify-center bg-line-soft">
        {src ? (
          <img
            src={src}
            alt={media.caption ?? label}
            loading="lazy"
            className="h-full w-full object-cover"
          />
        ) : (
          <div className="px-3 text-center">
            <p className="text-xs font-medium text-ink-soft">{label}</p>
            <p className="mt-1 text-[11px] leading-relaxed text-ink-faint">
              {media.uploadState === "stored"
                ? "جارٍ تحميل الصورة…"
                : media.uploadState === "failed"
                  ? "فشل رفع الصورة من جهاز الفني."
                  : "الصورة لم تصل بعد. جهاز الفني ما زال يرفعها."}
            </p>
          </div>
        )}
        <Badge tone={TONE[media.uploadState]} className="absolute top-1.5 right-1.5">
          {UPLOAD_STATE_AR[media.uploadState]}
        </Badge>
      </div>
      <figcaption className="border-t border-line px-2 py-1.5">
        <p className="truncate text-xs font-medium text-ink">{media.caption || label}</p>
        <p className="num text-[11px] text-ink-faint">{fmtDateTime(media.capturedAt)}</p>
      </figcaption>
    </figure>
  );
}

/** Shown once per screen that renders photos, so the gap is never mistaken for missing data. */
export function MediaViewingNote({ storedCount }: { storedCount: number }) {
  if (MEDIA_VIEWING_CONFIGURED || storedCount === 0) return null;
  return (
    <Note className="mb-3">
      <span className="num">{storedCount}</span> صورة محفوظة في الخزن لكن لا يمكن عرضها: الـ API لا
      يوفّر منفذاً لعرض الصور بعد. البيانات موجودة ولم تُفقد.
    </Note>
  );
}
